import { formatDateLabel } from './eventTemporal';

const NO_DATE = "The source doesn't show a date for this event.";
const NOT_TIED = "The date in the source isn't clearly tied to this event.";
const UNSETTLED = "The source check couldn't settle this date.";
const NOT_ANNOUNCED = "The source doesn't announce this date.";
const DATES_DIFFER = 'The sources disagree on the date.';
const TIMES_DIFFER = 'The sources list different start times.';

/**
 * Plain wording for stored review reasons, keyed without their `date-unverified:`
 * style prefix. Display only: this never decides what is flagged. An empty value
 * means the reason adds nothing to its label.
 */
const REASONS: Readonly<Record<string, string>> = {
  no_date_evidence: NO_DATE,
  'no-source-date': NO_DATE,
  needs_review: UNSETTLED,
  uncertain: UNSETTLED,
  'source-date-uncertain': 'The date in the source is unclear.',
  contradicted: 'The source shows a different date.',
  'not-announced': NOT_ANNOUNCED,
  'not-an-announced-occurrence': NOT_ANNOUNCED,
  'date-quote-does-not-support-date': "The source text doesn't match this date.",
  'source-citation-is-another-activity': 'The date in the source belongs to a different event.',
  'quote-not-activity-local': NOT_TIED,
  'date-quote-not-activity-local': NOT_TIED,
  'source-date-not-bound-to-activity': NOT_TIED,
  'source-image-positions-ambiguous-blocks': "The flyer lists several events, and it's unclear which date is this one's.",
  'activity-not-found': "This event wasn't found in the source.",
  'source-date-conflict': DATES_DIFFER,
  'source-date-verification-unavailable': "The source couldn't be checked.",
  'image-date-not-independently-verified': "The date comes from an image and hasn't been checked separately.",
  'calendar-period-uncertain': "It's unclear which month this calendar covers.",
  'no-source-time': "The source doesn't list a start time.",
  'quote-not-grounded': "This time wasn't found in the source.",
  'source-time-unverified': "The start time couldn't be confirmed.",
  'conflicting-source-times': TIMES_DIFFER,
  'clock-mismatch': 'The source shows a different start time.',
  'ambiguous-clock': 'The start time in the source is unclear.',
  'source-clock-disagrees-with-all-day': 'The source shows a start time, but this is saved as all day.',
  'opening-date-not-stated': "The source doesn't say which day this starts.",
  'Actual activity-local source dates/printed weekdays conflict': DATES_DIFFER,
  'Actual source starting clocks conflict; no clock was automatically chosen': TIMES_DIFFER,
  'Current source date is missing, invalid, unbound or not supported by every known source': "The source doesn't clearly support this date.",
  'Starting clock is missing or not independently bound to this occurrence': "The source doesn't clearly support this start time.",
  'Actual reader output is invalid/refused/incomplete; provider success is not proof': "The source check didn't return a usable result.",
  'Reader output is not admissible clock proof': "The source check didn't return a usable result.",
  'Literal source dates support the current New York calendar value in every contributing source': 'The source supports this date.',
  'Literal activity-local clock supports the current New York clock': 'The source supports this time.',
  'All-day event; there is no chosen/verified clock': 'This is saved as all day because no start time was confirmed.',
  'A durable deterministic warning-confirmation receipt supports this exact current field': 'The source supports this value.',
  'Source clock needs gap/fold review; only its calendar date is selectable': 'Only the date can be used. This time falls in a daylight-saving change.',
  'Literal activity-local source claim': '',
};

const title = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

function parameterizedReason(code: string): string | null {
  const [kind, value = ''] = code.split(/:(.*)/s);
  const [claimed, actual] = value.split('-vs-');
  switch (kind) {
    case 'corrected-suggests': {
      const date = formatDateLabel(value, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      return date ? `The source check suggests ${date}.` : UNSETTLED;
    }
    case 'quote-mismatch': {
      const days = Math.abs(Number.parseInt(value, 10));
      return days ? `The date in the source is ${days} day${days === 1 ? '' : 's'} from this date.` : "The source text doesn't match this date.";
    }
    case 'weekday-title-mismatch':
      return actual ? `The title says ${title(claimed)}, but this date is a ${title(actual)}.` : null;
    case 'calendar-weekday-mismatch':
      return actual ? `The calendar shows ${title(claimed)}, but this date is a ${title(actual)}.` : null;
    case 'calendar-weekday-missing':
      return "The calendar doesn't show a weekday for this date.";
    case 'calendar-month-mismatch':
      return "The calendar's month doesn't match this date.";
    case 'calendar-month-missing':
      return "The calendar doesn't show which month this is.";
    case 'calendar-year-mismatch':
      return "The calendar's year doesn't match this date.";
    default:
      return code.startsWith('recurrence-') ? "This repeating event's date couldn't be confirmed." : null;
  }
}

/** Null means the caller should use its generic wording: raw codes are never shown. */
export function dateProofReasonText(reason: string | null | undefined): string | null {
  const text = reason?.trim();
  if (!text) return null;
  if (text.startsWith('This exact field was explicitly chosen by a human')) {
    return /contrary evidence|conflicting source clocks/.test(text) ? 'Chosen by hand. The sources still disagree.' : 'Chosen by hand.';
  }
  const code = text.replace(/^(?:date-unverified|date-verifier|time-unverified):/, '');
  const known = REASONS[code];
  if (known !== undefined) return known || null;
  const parameterized = parameterizedReason(code);
  if (parameterized) return parameterized;
  return code === text && /\s/.test(text) ? text : null;
}
